import { type StateCreator } from "zustand";

import { userService } from "@/api/user";
import type { TGuidancePatchPayload } from "@/api/user/guidance.types";
import { mergeGuidanceServerWins, toPersistedState } from "@/lib/guidance/guidanceSync";
import { guidanceStorage } from "@/lib/guidance/guidanceStorage";
import {
  EMPTY_GUIDANCE_STATE,
  type TBannerId,
  type TGuidanceEvent,
  type TGuidancePersistedState,
  type TTooltipId,
  type TTourId,
  type TTourStatus,
} from "@/lib/guidance/types";

export type TGuidanceStoreState = {
  guidanceUserId: string | null;
  guidanceState: TGuidancePersistedState;
  activeTourId: TTourId | null;
  isGuidanceHydrated: boolean;
};

export type TGuidanceStoreActions = {
  hydrateGuidance: (userId: string) => Promise<void>;
  resetGuidance: () => void;
  markTour: (tourId: TTourId, status: TTourStatus) => void;
  markTooltipSeen: (tooltipId: TTooltipId) => void;
  markBannerDismissed: (bannerId: TBannerId) => void;
  setActiveTourId: (tourId: TTourId | null) => void;
  isTourFinished: (tourId: TTourId) => boolean;
  isTooltipSeen: (tooltipId: TTooltipId) => boolean;
  isBannerDismissed: (bannerId: TBannerId) => boolean;
  emitGuidanceEvent: (event: TGuidanceEvent) => void;
};

export type TGuidanceStore = TGuidanceStoreState & TGuidanceStoreActions;

const initialGuidanceStoreState: TGuidanceStoreState = {
  guidanceUserId: null,
  guidanceState: { ...EMPTY_GUIDANCE_STATE },
  activeTourId: null,
  isGuidanceHydrated: false,
};

const persistGuidanceState = (userId: string | null, state: TGuidancePersistedState) => {
  if (!userId) {
    return;
  }
  guidanceStorage.write(userId, state);
};

const syncGuidancePatch = (
  userId: string | null,
  payload: TGuidancePatchPayload,
  onSuccess?: (state: TGuidancePersistedState) => void
) => {
  if (!userId) {
    return;
  }

  void userService.patchGuidance(payload).then((result) => {
    if (result.error) {
      console.warn("[guidance] Failed to persist guidance change", {
        payload,
        error: result.error,
      });
      return;
    }

    if (result.data) {
      const persistedState = toPersistedState(result.data);
      persistGuidanceState(userId, persistedState);
      onSuccess?.(persistedState);
    }
  });
};

export const createGuidanceSlice: StateCreator<TGuidanceStore> = (set, get) => ({
  ...initialGuidanceStoreState,

  hydrateGuidance: async (userId: string) => {
    const currentUserId = get().guidanceUserId;
    if (currentUserId === userId && get().isGuidanceHydrated) {
      return;
    }

    const localState = guidanceStorage.read(userId);
    const result = await userService.getGuidance();

    if (result.error) {
      console.warn("[guidance] Failed to hydrate from server", { error: result.error });
      set({
        guidanceUserId: userId,
        guidanceState: localState,
        isGuidanceHydrated: true,
        activeTourId: null,
      });
      return;
    }

    const serverState = result.data ? toPersistedState(result.data) : { ...EMPTY_GUIDANCE_STATE };
    const mergedState = mergeGuidanceServerWins(localState, serverState);
    persistGuidanceState(userId, mergedState);

    set({
      guidanceUserId: userId,
      guidanceState: mergedState,
      isGuidanceHydrated: true,
      activeTourId: null,
    });
  },

  resetGuidance: () => {
    set({ ...initialGuidanceStoreState });
  },

  markTour: (tourId, status) => {
    const { guidanceUserId, guidanceState } = get();
    const nextState: TGuidancePersistedState = {
      ...guidanceState,
      tours: { ...guidanceState.tours, [tourId]: status },
    };

    set({ guidanceState: nextState, activeTourId: null });
    persistGuidanceState(guidanceUserId, nextState);
    syncGuidancePatch(guidanceUserId, { tours: { [tourId]: status } }, (persistedState) => {
      set({ guidanceState: persistedState });
    });

    get().emitGuidanceEvent({
      type: "tour",
      id: tourId,
      action: status === "completed" ? "completed" : "dismissed",
    });
  },

  markTooltipSeen: (tooltipId) => {
    const { guidanceUserId, guidanceState } = get();
    if (guidanceState.tooltips[tooltipId]) {
      return;
    }

    const nextState: TGuidancePersistedState = {
      ...guidanceState,
      tooltips: { ...guidanceState.tooltips, [tooltipId]: true },
    };

    set({ guidanceState: nextState });
    persistGuidanceState(guidanceUserId, nextState);
    syncGuidancePatch(guidanceUserId, { tooltips: { [tooltipId]: true } }, (persistedState) => {
      set({ guidanceState: persistedState });
    });

    get().emitGuidanceEvent({ type: "tooltip", id: tooltipId, action: "dismissed" });
  },

  markBannerDismissed: (bannerId) => {
    const { guidanceUserId, guidanceState } = get();
    if (guidanceState.banners[bannerId]) {
      return;
    }

    const nextState: TGuidancePersistedState = {
      ...guidanceState,
      banners: { ...guidanceState.banners, [bannerId]: true },
    };

    set({ guidanceState: nextState });
    persistGuidanceState(guidanceUserId, nextState);
    syncGuidancePatch(guidanceUserId, { banners: { [bannerId]: true } }, (persistedState) => {
      set({ guidanceState: persistedState });
    });

    get().emitGuidanceEvent({ type: "banner", id: bannerId, action: "dismissed" });
  },

  setActiveTourId: (tourId) => {
    set({ activeTourId: tourId });
    if (tourId) {
      get().emitGuidanceEvent({ type: "tour", id: tourId, action: "started" });
    }
  },

  isTourFinished: (tourId) => {
    const status = get().guidanceState.tours[tourId];
    return status === "completed" || status === "dismissed";
  },

  isTooltipSeen: (tooltipId) => Boolean(get().guidanceState.tooltips[tooltipId]),

  isBannerDismissed: (bannerId) => Boolean(get().guidanceState.banners[bannerId]),

  emitGuidanceEvent: (event) => {
    console.info("[guidance] Guidance event", event);
  },
});
